import { type FleetPulseData } from '@/hooks/admin/useAdminFleetPulse';
import { AdminTabShell } from './AdminTabShell';

interface FleetPulseTabProps {
  data: FleetPulseData | null;
  isLoading: boolean;
  error: Error | null;
}

function formatRelativeTime(iso: string | null): string {
  if (!iso) return 'Never';
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'Just now';
  if (min < 60) return `${min} min ago`;
  const hrs = Math.floor(min / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    stale: 'bg-wt-amber-primary/20 text-wt-amber-primary',
    offline: 'bg-wt-red-primary/20 text-wt-red-primary',
    never: 'bg-wt-red-primary/20 text-wt-red-primary',
    unknown: 'bg-gray-500/20 text-gray-400',
  };
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-mono uppercase ${colors[status] ?? colors.unknown}`}>
      {status}
    </span>
  );
}

export function FleetPulseTab({ data, isLoading, error }: FleetPulseTabProps) {
  return (
    <AdminTabShell isLoading={isLoading} error={error}>
      {data && (
        <div className="space-y-4">
          {/* Summary bar */}
          <div className="flex items-center gap-4 text-sm font-mono">
            <span className="text-wt-green-primary">{data.summary.live} live</span>
            <span className="text-wt-amber-primary">{data.summary.stale} stale</span>
            <span className="text-wt-red-primary">{data.summary.offline} offline</span>
            <span className="text-wt-text-muted">{data.summary.never} never polled</span>
            <span className="text-wt-text-disabled ml-auto">{data.totalProperties} total properties</span>
          </div>

          {/* Problem properties table */}
          {data.problemProperties.length === 0 ? (
            <div className="text-center py-12 text-sm text-wt-text-muted">
              All properties reporting. No issues detected.
            </div>
          ) : (
            <div className="border border-wt-border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-wt-bg-tertiary text-wt-text-muted text-xs uppercase tracking-wider">
                    <th className="px-3 py-2 text-left">Property</th>
                    <th className="px-3 py-2 text-left">Org</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-right">Last Data</th>
                    <th className="px-3 py-2 text-right">Minutes Stale</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-wt-border">
                  {data.problemProperties.map((p) => (
                    <tr key={p.id} className="hover:bg-wt-bg-secondary/50">
                      <td className="px-3 py-2">
                        <a
                          href={`/app/property/${p.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-wt-text-primary hover:underline"
                        >
                          {p.name}
                        </a>
                      </td>
                      <td className="px-3 py-2 text-wt-text-secondary">{p.org_name}</td>
                      <td className="px-3 py-2">
                        <StatusBadge status={p.poll_status} />
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-wt-text-secondary">
                        {formatRelativeTime(p.last_telemetry_at)}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-wt-text-muted">
                        {p.minutes_stale ?? '--'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </AdminTabShell>
  );
}
